/**
 * Caregiver PWA — mirror of the Driver PWA, adapted for home care visits.
 *
 * Two entry paths:
 *   1. `/caregiver` — 6-character invite-code login (issued by admin).
 *   2. `/caregiver/{token}` — tokenized deep link emailed by admin.
 *
 * Once authed, caregivers see today's scheduled visits and can move each
 * one through the lifecycle: en_route → arrived → in_progress → completed.
 * On complete they capture care notes + a client signature.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, MapPin, CheckCircle2, Loader2, LogOut, Phone, ChevronRight, Home, Clock, Bell, BellRing,
} from "lucide-react";
import { API } from "@/lib/api";
import {
  registerCaregiverServiceWorker, subscribeCaregiverToPush, pushSupported, playAlarm,
} from "@/lib/caregiverPush";

const AUTH_KEY = "caregiver_token";
const CG_KEY = "caregiver_profile";

const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

async function api(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.message || `Server ${res.status}`);
  return data;
}

/* ==================== Invite-code sign in ==================== */

function InviteGate({ onAuth }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const data = await api("/homecare/caregiver/login", {
        method: "POST",
        body: { invite_code: code.trim().toUpperCase() },
      });
      localStorage.setItem(AUTH_KEY, data.token);
      localStorage.setItem(CG_KEY, JSON.stringify(data.caregiver));
      onAuth(data.token, data.caregiver);
    } catch (err) {
      toast.error(err.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-brand-deep text-white flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-emerald-300 font-semibold">Caregiver</div>
            <div className="font-serif text-2xl">1st Touch Home Care</div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="caregiver-login-form" className="bg-white/5 border border-white/10 rounded-3xl p-6">
          <label className="block text-xs uppercase tracking-[0.18em] text-white/60 mb-3">Invite code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            autoFocus
            data-testid="caregiver-login-code"
            className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/15 text-white text-lg tracking-[0.22em] placeholder-white/25 focus:outline-none focus:border-emerald-400"
          />
          <button type="submit" disabled={loading} data-testid="caregiver-login-submit"
            className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-brand-deep font-semibold py-3 rounded-2xl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
          <p className="text-xs text-white/60 mt-4 text-center">
            Didn't receive your code? Ask your dispatcher to resend it.
          </p>
        </form>
      </div>
    </div>
  );
}

/* ==================== Signature pad ==================== */

function SignaturePad({ visitId, signed, onSaved }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [saved, setSaved] = useState(signed);
  const [busy, setBusy] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [notes, setNotes] = useState("");
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#003366";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width),
             y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const down = (e) => { e.preventDefault(); setDrawing(true); last.current = pos(e); };
  const move = (e) => {
    if (!drawing) return; e.preventDefault();
    const c = canvasRef.current, ctx = c.getContext("2d"), p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; setHasStrokes(true);
  };
  const up = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current, ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height); setHasStrokes(false);
  };
  const save = async () => {
    if (!hasStrokes) { toast.error("Please have the client sign first."); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem(AUTH_KEY);
      const data = canvasRef.current.toDataURL("image/png");
      await api(`/homecare/caregiver/${token}/visits/${visitId}/status`, {
        method: "POST",
        body: {
          status: "completed",
          care_notes: notes || undefined,
          signature_data_url: data,
          signed_by_name: signerName || undefined,
        },
      });
      setSaved(true);
      toast.success("Visit completed");
      onSaved && onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  if (saved) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 flex items-center gap-2" data-testid="cg-sig-saved">
        <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        <span className="text-sm font-medium text-emerald-900">Visit completed &amp; signature captured.</span>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 space-y-3" data-testid="cg-sig-pad">
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold mb-1">Care notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={3} placeholder="Vitals, tasks completed, observations…"
          data-testid="cg-care-notes"
          className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold mb-1">Client signature</label>
        <input
          value={signerName} onChange={(e) => setSignerName(e.target.value)}
          placeholder="Signer's name (client or family)"
          data-testid="cg-signer-name"
          className="w-full px-3 py-2 mb-2 text-sm rounded-lg border border-brand-navy/15"
        />
        <div className="relative border-2 border-dashed border-brand-navy/20 rounded-lg bg-brand-surfaceAlt/30" style={{ touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            width={600} height={180}
            data-testid="cg-sig-canvas"
            onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
            onTouchStart={down} onTouchMove={move} onTouchEnd={up}
            className="w-full h-40 rounded-lg"
          />
          {!hasStrokes && (
            <div className="absolute inset-0 flex items-center justify-center text-brand-muted text-xs pointer-events-none">
              Sign above
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={clear} data-testid="cg-sig-clear" className="text-xs text-brand-muted hover:text-brand-navy px-3 py-1.5 rounded-full">Clear</button>
          <button onClick={save} disabled={!hasStrokes || busy} data-testid="cg-sig-save"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Complete visit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== Single visit card ==================== */

const STATUS_TONE = {
  scheduled: "bg-slate-100 text-slate-800",
  en_route:  "bg-amber-100 text-amber-800",
  arrived:   "bg-blue-100 text-blue-800",
  in_progress: "bg-emerald-100 text-emerald-800",
  completed: "bg-emerald-200 text-emerald-900",
};

function VisitCard({ visit, token, onRefresh }) {
  const [busy, setBusy] = useState("");
  const status = visit.driver_ride_status || visit.status || "scheduled";
  const setStatus = async (next) => {
    setBusy(next);
    try {
      await api(`/homecare/caregiver/${token}/visits/${visit.id}/status`, {
        method: "POST", body: { status: next },
      });
      toast.success(`Marked ${next.replace("_", " ")}`);
      onRefresh && onRefresh();
    } catch (e) { toast.error(e.message || "Update failed"); }
    finally { setBusy(""); }
  };

  const nextAction = {
    scheduled: { next: "en_route", label: "Head to client", color: "bg-amber-500 hover:bg-amber-600" },
    en_route: { next: "arrived", label: "I've arrived", color: "bg-blue-600 hover:bg-blue-700" },
    arrived: { next: "in_progress", label: "Start visit", color: "bg-emerald-600 hover:bg-emerald-700" },
    in_progress: null, // final step is signature
  }[status];

  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 space-y-3 shadow-sm" data-testid={`cg-visit-${visit.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">
            {visit.caregiver_type} · {titleCase(visit.visit_type || "")}
          </div>
          <div className="font-serif text-lg text-brand-deep truncate">
            {visit.member_name || "Home care visit"}
          </div>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-[0.14em] ${STATUS_TONE[status] || "bg-slate-100 text-slate-700"}`}
          data-testid={`cg-visit-status-${visit.id}`}>
          {status.replace("_", " ")}
        </span>
      </div>

      <div className="text-sm text-brand-deep space-y-1">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
          <span className="text-brand-muted">{visit.address}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-muted">
          <Clock className="w-3.5 h-3.5" />
          {new Date(visit.start_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          {visit.duration_minutes ? ` · ${visit.duration_minutes} min` : null}
          {visit.days_count ? ` · ${visit.days_count} days` : null}
        </div>
        {visit.member_phone ? (
          <a href={`tel:${visit.member_phone}`} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline" data-testid={`cg-call-${visit.id}`}>
            <Phone className="w-3.5 h-3.5" /> {visit.member_phone}
          </a>
        ) : null}
        {visit.task_notes ? (
          <div className="text-xs bg-brand-surfaceAlt/40 border border-brand-navy/10 rounded p-2 mt-2">
            <span className="font-semibold">Task notes: </span>{visit.task_notes}
          </div>
        ) : null}
      </div>

      {nextAction && (
        <button onClick={() => setStatus(nextAction.next)} disabled={!!busy}
          data-testid={`cg-advance-${visit.id}`}
          className={`w-full inline-flex items-center justify-center gap-2 ${nextAction.color} disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-full`}>
          {busy === nextAction.next ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          {nextAction.label}
        </button>
      )}

      {status === "in_progress" && (
        <SignaturePad visitId={visit.id} signed={!!visit.signature_data_url} onSaved={onRefresh} />
      )}
    </div>
  );
}

/* ==================== Main app ==================== */

function CaregiverApp({ token, caregiver, onLogout }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushState, setPushState] = useState(() =>
    !pushSupported() ? "unsupported" :
    Notification.permission === "granted" ? "granted" :
    Notification.permission === "denied" ? "denied" : "prompt"
  );
  const [pushBusy, setPushBusy] = useState(false);
  const prevIds = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/homecare/caregiver/${token}/visits`);
      const list = data.visits || [];
      // Fire in-app alarm if a new visit appeared since last poll (fallback for push-denied users)
      const nextIds = new Set(list.map((v) => v.id));
      if (prevIds.current.size > 0) {
        for (const id of nextIds) if (!prevIds.current.has(id)) { playAlarm(); break; }
      }
      prevIds.current = nextIds;
      setVisits(list);
    } catch (e) {
      if (String(e.message).toLowerCase().includes("invalid caregiver token")) {
        toast.error("Session expired — sign in again");
        onLogout && onLogout();
      } else toast.error(e.message || "Failed to load visits");
    } finally { setLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { load(); const id = setInterval(load, 30_000); return () => clearInterval(id); }, [load]);

  // Register service worker on mount (needed even if user hasn't granted permission yet)
  useEffect(() => { registerCaregiverServiceWorker(); }, []);

  // If push was previously granted, re-subscribe silently so we stay current after redeploys
  useEffect(() => {
    if (pushState === "granted") {
      subscribeCaregiverToPush(token).catch(() => {});
    }
  }, [pushState, token]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const r = await subscribeCaregiverToPush(token);
      if (r.ok) { setPushState("granted"); toast.success("Notifications enabled"); }
      else if (r.reason === "denied") { setPushState("denied"); toast.error("Enable notifications in your browser settings, then reload."); }
      else if (r.reason === "unsupported") { toast.error("This browser does not support push notifications."); }
      else { toast.error(`Enable failed: ${r.reason}`); }
    } finally { setPushBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="caregiver-app">
      <header className="bg-brand-deep text-white px-5 py-5">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Caregiver</div>
              <div className="font-serif text-lg">{caregiver?.name || "Hi"} · {caregiver?.caregiver_type}</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="caregiver-logout"
            className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-4">
        {/* Push notifications banner */}
        {pushState !== "granted" && pushState !== "unsupported" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3" data-testid="cg-push-banner">
            <Bell className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900">Get instant visit alerts</div>
              <div className="text-xs text-emerald-800 mt-0.5">
                {pushState === "denied"
                  ? "Notifications are blocked. Enable them in your browser settings and reload."
                  : "Turn on browser notifications so you never miss a new assignment."}
              </div>
            </div>
            {pushState !== "denied" && (
              <button onClick={enablePush} disabled={pushBusy} data-testid="cg-push-enable"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                Enable
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-brand-deep">Today's visits</h2>
          <span className="text-xs text-brand-muted" data-testid="cg-visit-count">
            {loading ? "Loading…" : `${visits.length} visit${visits.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {!loading && visits.length === 0 && (
          <div className="bg-white border border-brand-navy/10 rounded-2xl p-8 text-center" data-testid="cg-no-visits">
            <Home className="w-8 h-8 text-brand-muted mx-auto mb-2" />
            <div className="text-sm text-brand-muted">
              No visits scheduled right now. Dispatch will notify you when a new visit is assigned.
            </div>
          </div>
        )}

        {visits.map((v) => (
          <VisitCard key={v.id} visit={v} token={token} onRefresh={load} />
        ))}
      </main>
    </div>
  );
}

/* ==================== Route root ==================== */

export default function Caregiver() {
  const { token: routeToken } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(routeToken || localStorage.getItem(AUTH_KEY) || "");
  const [caregiver, setCaregiver] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CG_KEY) || "null"); } catch { return null; }
  });
  const [hydrating, setHydrating] = useState(!!routeToken && !caregiver);

  // If URL has a token but we don't have a caregiver record yet, hydrate.
  useEffect(() => {
    let cancelled = false;
    if (routeToken && !caregiver) {
      (async () => {
        try {
          const data = await api(`/homecare/caregiver/${routeToken}/me`);
          if (cancelled) return;
          localStorage.setItem(AUTH_KEY, routeToken);
          localStorage.setItem(CG_KEY, JSON.stringify(data.caregiver));
          setCaregiver(data.caregiver);
          setToken(routeToken);
        } catch (e) {
          toast.error(e.message || "Invalid caregiver link");
          navigate("/caregiver", { replace: true });
        } finally { if (!cancelled) setHydrating(false); }
      })();
    }
    return () => { cancelled = true; };
  }, [routeToken, caregiver, navigate]);

  const onAuth = (tok, cg) => { setToken(tok); setCaregiver(cg); };
  const onLogout = () => {
    localStorage.removeItem(AUTH_KEY); localStorage.removeItem(CG_KEY);
    setToken(""); setCaregiver(null);
    navigate("/caregiver", { replace: true });
  };

  if (hydrating) return (
    <div className="min-h-screen bg-brand-deep flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-emerald-300 animate-spin" />
    </div>
  );
  if (!token || !caregiver) return <InviteGate onAuth={onAuth} />;
  return <CaregiverApp token={token} caregiver={caregiver} onLogout={onLogout} />;
}
